(() => {
  const theme = (window.theme = window.theme || {});
  const root = document.documentElement;
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const mqDesktop = window.matchMedia('(min-width: 1024px)');
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  theme.motion = () => (mqReduce.matches ? 'off' : root.dataset.motion || 'editorial');
  theme.isDesktop = () => mqDesktop.matches;

  const interpolate = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  theme.t = (key, values = {}) => interpolate(strings[key], values);

  theme.announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    clearTimeout(theme.liveTimer);
    region.textContent = '';
    theme.liveTimer = setTimeout(() => {
      region.textContent = message;
    }, 80);
  };

  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const focusables = (el) =>
    Array.from(el.querySelectorAll(FOCUSABLE)).filter(
      (node) => node.getAttribute('tabindex') !== '-1' && !node.closest('[inert]') && node.getClientRects().length > 0
    );
  theme.focusables = focusables;

  const overlayStack = [];

  const syncOverlayTriggers = () => {
    document.querySelectorAll('[data-overlay-open]').forEach((trigger) => {
      const target = document.getElementById(trigger.dataset.overlayOpen);
      if (target) trigger.setAttribute('aria-expanded', String(!target.hidden));
    });
  };

  theme.openOverlay = (overlay, opener) => {
    if (!overlay) return;
    if (!overlay.hidden) {
      const top = overlayStack[overlayStack.length - 1];
      if (top === overlay) return;
    }
    overlayStack.slice().forEach((open) => {
      if (open !== overlay) theme.closeOverlay(open, { restoreFocus: false });
    });
    overlay.opener = opener && opener.isConnected ? opener : overlay.opener || document.activeElement;
    overlay.hidden = false;
    if (!overlayStack.includes(overlay)) overlayStack.push(overlay);
    root.classList.add('overlay-open');
    const dialog = overlay.querySelector('[role="dialog"]') || overlay;
    const target = dialog.querySelector('[data-autofocus]') || dialog.querySelector('button') || focusables(dialog)[0];
    if (target) target.focus({ preventScroll: true });
    syncOverlayTriggers();
    document.dispatchEvent(new CustomEvent('overlay:change', { detail: { overlay, open: true } }));
  };

  theme.closeOverlay = (overlay, { restoreFocus = true } = {}) => {
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    const index = overlayStack.indexOf(overlay);
    if (index > -1) overlayStack.splice(index, 1);
    if (!overlayStack.length) root.classList.remove('overlay-open');
    const opener = overlay.opener;
    overlay.opener = null;
    if (restoreFocus && opener && opener.isConnected) opener.focus({ preventScroll: true });
    syncOverlayTriggers();
    document.dispatchEvent(new CustomEvent('overlay:change', { detail: { overlay, open: false } }));
  };

  theme.hasOpenOverlay = () =>
    overlayStack.length > 0 || Boolean(document.querySelector('[data-quick-panel]:not([hidden])'));

  document.addEventListener('keydown', (event) => {
    const top = overlayStack[overlayStack.length - 1];
    if (event.key === 'Escape') {
      if (top) {
        event.preventDefault();
        theme.closeOverlay(top);
        return;
      }
      const panel = document.querySelector('[data-quick-panel]:not([hidden])');
      if (panel) closeQuickPanel(panel, true);
      return;
    }
    if (event.key !== 'Tab' || !top) return;
    const dialog = top.querySelector('[role="dialog"]') || top;
    const items = focusables(dialog);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const inside = dialog.contains(document.activeElement);
    if (event.shiftKey ? document.activeElement === first || !inside : document.activeElement === last || !inside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  });

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-overlay-open]');
    if (openTrigger) {
      const target = document.getElementById(openTrigger.dataset.overlayOpen);
      if (target) {
        event.preventDefault();
        theme.openOverlay(target, openTrigger);
      }
      return;
    }
    const closeTrigger = event.target.closest('[data-overlay-close]');
    if (closeTrigger) {
      const overlay = closeTrigger.closest('.overlay');
      if (closeTrigger.tagName === 'A' && overlay) {
        theme.closeOverlay(overlay, { restoreFocus: false });
        return;
      }
      theme.closeOverlay(overlay);
    }
  });

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section') || this;
      this.lastY = null;
      this.isHidden = false;
      this.isScrolled = null;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.onFocusIn = () => {
        if (!this.isHidden) return;
        this.isHidden = false;
        this.wrapper.classList.remove('is-hidden');
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      document.addEventListener('overlay:change', this.onScroll);
      this.wrapper.addEventListener('focusin', this.onFocusIn);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('overlay:change', this.onScroll);
      this.wrapper.removeEventListener('focusin', this.onFocusIn);
    }

    update() {
      const y = window.scrollY;
      const scrolled = y > 48;
      if (scrolled !== this.isScrolled) {
        this.isScrolled = scrolled;
        this.wrapper.classList.toggle('is-scrolled', scrolled);
      }
      const previous = this.lastY == null ? y : this.lastY;
      const delta = y - previous;
      let hide = this.isHidden;
      if (theme.hasOpenOverlay() || y < 140) hide = false;
      else if (delta > 5) hide = true;
      else if (delta < -5) hide = false;
      if (Math.abs(delta) > 3 || this.lastY == null) this.lastY = y;
      if (hide !== this.isHidden) {
        this.isHidden = hide;
        this.wrapper.classList.toggle('is-hidden', hide);
      }
    }
  }
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-announcement]'));
      this.toggle = this.querySelector('[data-announcement-toggle]');
      this.index = 0;
      const canRotate = this.dataset.rotate === 'true' && this.items.length > 1 && theme.motion() !== 'off';
      if (!canRotate) {
        this.setStatic(true);
        if (this.toggle) this.toggle.hidden = true;
        return;
      }
      this.toggle?.addEventListener('click', () => this.setPaused(this.getAttribute('data-paused') !== 'true'));
      this.start();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    start() {
      clearInterval(this.timer);
      this.timer = setInterval(() => this.advance(), Number(this.dataset.interval || 4500));
    }

    advance() {
      const current = this.items[this.index];
      this.index = (this.index + 1) % this.items.length;
      const next = this.items[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      setTimeout(() => current.classList.remove('is-leaving'), 800);
    }

    setStatic(isStatic) {
      this.classList.toggle('is-static', isStatic);
    }

    setPaused(paused) {
      this.setAttribute('data-paused', String(paused));
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.setAttribute('aria-label', paused ? strings.announcementPlay : strings.announcementPause);
      }
      this.setStatic(paused);
      if (paused) clearInterval(this.timer);
      else this.start();
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  theme.initReveal = (scope = document) => {
    const elements = Array.from(scope.querySelectorAll('[data-reveal]:not([data-revealed])'));
    if (!elements.length) return;
    const reveal = (el) => {
      el.setAttribute('data-revealed', '');
      el.style.opacity = '';
      el.style.transform = '';
    };
    if (theme.motion() === 'off' || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      elements.forEach(reveal);
      return;
    }
    theme.revealObserver =
      theme.revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            theme.revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px' }
      );
    elements.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
        reveal(el);
        return;
      }
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition = 'opacity .9s cubic-bezier(.2,.6,.2,1), transform .9s cubic-bezier(.2,.6,.2,1)';
      theme.revealObserver.observe(el);
    });
  };

  const nativeHls = navigator.vendor === 'Apple Computer, Inc.';

  theme.loadVideo = (video) => {
    if (!video || video.dataset.videoLoaded) return video;
    video.dataset.videoLoaded = 'true';
    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!nativeHls && /mpegurl/i.test(source.type)) {
        source.remove();
        return;
      }
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
    return video;
  };

  theme.canAutoplayVideo = () => {
    if (theme.motion() === 'off') return false;
    const connection = navigator.connection;
    if (!connection) return true;
    if (connection.saveData) return false;
    if (connection.effectiveType && connection.effectiveType !== '4g') return false;
    return !(connection.downlink && connection.downlink < 3);
  };

  theme.playVideo = (video) => {
    if (!video) return;
    theme.loadVideo(video);
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => {});
  };

  const ambientObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && theme.canAutoplayVideo()) theme.playVideo(entry.target);
              else if (!entry.target.paused) entry.target.pause();
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  theme.initAmbientVideos = (scope = document) => {
    if (!ambientObserver) return;
    scope.querySelectorAll('video[data-autoplay]:not([data-video-managed])').forEach((video) => {
      if (video.closest('[data-video-controller]')) return;
      video.setAttribute('data-video-managed', '');
      ambientObserver.observe(video);
    });
  };

  const cartDrawer = () => document.getElementById('CartDrawer');

  theme.cart = {
    sections: () => ['cart-drawer'],

    async post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sections(), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError);
        error.data = data;
        throw error;
      }
      return data;
    },

    add(items) {
      return this.post(`${routes.cart_add_url}.js`, { items }).then((data) => {
        this.render(data.sections);
        return data;
      });
    },

    change(line, quantity) {
      return this.post(`${routes.cart_change_url}.js`, { line, quantity }).then((data) => {
        this.render(data.sections);
        return data;
      });
    },

    update(body) {
      return this.post(`${routes.cart_update_url}.js`, body).then((data) => {
        this.render(data.sections);
        return data;
      });
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      const drawer = cartDrawer();
      if (!html || !drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const count = Number(doc.querySelector('[data-cart-count-value]')?.dataset.cartCountValue || 0);
      const fresh = doc.querySelector('[data-cart-content]');
      const current = drawer.querySelector('[data-cart-content]');
      if (fresh && current) current.replaceWith(fresh);
      this.setCount(count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },

    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = count > 0 ? String(count) : '';
      });
      const label = count > 0 ? interpolate(count === 1 ? strings.bagOne : strings.bagOther, { count }) : strings.bag;
      document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => trigger.setAttribute('aria-label', label));
    },

    open(opener) {
      const drawer = cartDrawer();
      if (drawer) theme.openOverlay(drawer, opener);
    },
  };

  const setBusy = (element, busy) => {
    if (!element) return;
    element.toggleAttribute('aria-busy', busy);
    if ('disabled' in element) element.disabled = busy;
  };

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      setBusy(remove, true);
      const title = remove.dataset.title;
      try {
        await theme.cart.change(Number(remove.dataset.line), 0);
        theme.announce(interpolate(strings.cartRemoved, { title }));
        cartDrawer()?.querySelector('[data-cart-focus]')?.focus({ preventScroll: true });
      } catch (error) {
        setBusy(remove, false);
        theme.announce(error.message || strings.cartError);
      }
      return;
    }

    const upsell = event.target.closest('[data-cart-upsell]');
    if (upsell) {
      event.preventDefault();
      setBusy(upsell, true);
      try {
        await theme.cart.add([{ id: Number(upsell.dataset.variantId), quantity: 1 }]);
        theme.announce(interpolate(strings.added, { title: upsell.dataset.title }));
        cartDrawer()?.querySelector('[data-cart-focus]')?.focus({ preventScroll: true });
      } catch (error) {
        setBusy(upsell, false);
        theme.announce(error.message || strings.cartError);
      }
    }
  });

  const setDiscountStatus = (message) => {
    const status = cartDrawer()?.querySelector('[data-discount-status]');
    if (status) status.textContent = message;
    theme.announce(message);
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-cart-discount]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = input.value.trim();
    if (!code) return;
    const button = form.querySelector('button[type="submit"]');
    setBusy(button, true);
    try {
      const existing = Array.from(form.querySelectorAll('[data-discount-code]')).map((pill) => pill.dataset.discountCode);
      const codes = Array.from(new Set([...existing, code]));
      const data = await theme.cart.update({ discount: codes.join(',') });
      const applied = Array.isArray(data.discount_codes)
        ? data.discount_codes.find((entry) => entry.code.toLowerCase() === code.toLowerCase())
        : null;
      if (applied && applied.applicable) {
        setDiscountStatus(interpolate(strings.discountApplied, { code: applied.code }));
      } else if (applied) {
        await theme.cart.update({ discount: existing.join(',') });
        setDiscountStatus(strings.discountInvalid);
      } else {
        await fetch(`${routes.root}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
        setDiscountStatus(interpolate(strings.discountSaved, { code }));
      }
    } catch (error) {
      setDiscountStatus(error.message || strings.cartError);
    } finally {
      setBusy(button, false);
    }
  });

  document.addEventListener('click', async (event) => {
    const pill = event.target.closest('[data-discount-remove]');
    if (!pill) return;
    event.preventDefault();
    const form = pill.closest('[data-cart-discount]');
    const remaining = Array.from(form.querySelectorAll('[data-discount-code]'))
      .map((node) => node.dataset.discountCode)
      .filter((code) => code !== pill.dataset.discountRemove);
    try {
      await theme.cart.update({ discount: remaining.join(',') });
    } catch (error) {
      theme.announce(error.message || strings.cartError);
    }
  });

  const quickSheet = () => document.getElementById('QuickAddSheet');

  function closeQuickPanel(panel, restoreFocus) {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    const card = panel.closest('[data-product-card]');
    const toggle = card?.querySelector('[data-quick-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) toggle.focus({ preventScroll: true });
    }
  }
  theme.closeQuickPanel = closeQuickPanel;

  const openQuickSheet = (card, toggle) => {
    const sheet = quickSheet();
    if (!sheet) return;
    const sizes = card.querySelector('[data-quick-sizes]');
    const title = card.dataset.productTitle || '';
    sheet.querySelector('[data-sheet-title]').textContent = title;
    sheet.querySelector('[data-sheet-price]').textContent = card.dataset.productPrice || '';
    sheet.querySelector('[role="dialog"]').setAttribute('aria-label', interpolate(strings.selectSizeFor, { title }));
    const thumb = sheet.querySelector('[data-sheet-thumb]');
    if (card.dataset.productThumb) {
      thumb.src = card.dataset.productThumb;
      thumb.hidden = false;
    } else {
      thumb.removeAttribute('src');
      thumb.hidden = true;
    }
    const target = sheet.querySelector('[data-sheet-sizes]');
    target.innerHTML = '';
    if (sizes) {
      sizes.querySelectorAll('[data-variant-id]').forEach((chip) => {
        const clone = chip.cloneNode(true);
        clone.className = 'size-chip size-chip--sheet';
        clone.dataset.productTitle = title;
        target.appendChild(clone);
      });
      const firstAvailable = target.querySelector('[data-variant-id]:not([disabled])');
      if (firstAvailable) firstAvailable.setAttribute('data-autofocus', '');
    }
    theme.openOverlay(sheet, toggle);
  };

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-quick-toggle]');
    if (toggle) {
      event.preventDefault();
      const card = toggle.closest('[data-product-card]');
      if (!card) return;
      if (!theme.isDesktop()) {
        openQuickSheet(card, toggle);
        return;
      }
      const panel = card.querySelector('[data-quick-panel]');
      if (!panel) return;
      const willOpen = panel.hidden;
      document.querySelectorAll('[data-quick-panel]:not([hidden])').forEach((open) => {
        if (open !== panel) closeQuickPanel(open, false);
      });
      panel.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        const first = panel.querySelector('[data-variant-id]:not([disabled])') || panel.querySelector('button');
        first?.focus({ preventScroll: true });
      }
      return;
    }

    const close = event.target.closest('[data-quick-close]');
    if (close) {
      event.preventDefault();
      closeQuickPanel(close.closest('[data-quick-panel]'), true);
      return;
    }

    const chip = event.target.closest('[data-variant-id][data-size]');
    if (!chip || chip.disabled) return;
    event.preventDefault();
    const card = chip.closest('[data-product-card]');
    const sheet = quickSheet();
    const fromSheet = Boolean(sheet && sheet.contains(chip));
    const title = chip.dataset.productTitle || card?.dataset.productTitle || '';
    const size = chip.dataset.size;
    const opener = fromSheet ? sheet.opener : card?.querySelector('[data-quick-toggle]');
    setBusy(chip, true);
    try {
      await theme.cart.add([{ id: Number(chip.dataset.variantId), quantity: 1 }]);
      if (card) closeQuickPanel(card.querySelector('[data-quick-panel]'), false);
      if (fromSheet) theme.closeOverlay(sheet, { restoreFocus: false });
      theme.cart.open(opener);
      theme.announce(size ? interpolate(strings.addedWithSize, { title, size }) : interpolate(strings.added, { title }));
    } catch (error) {
      theme.announce(error.message || strings.cartError);
    } finally {
      setBusy(chip, false);
    }
  });

  document.addEventListener('click', (event) => {
    const openPanel = document.querySelector('[data-quick-panel]:not([hidden])');
    if (!openPanel) return;
    const card = openPanel.closest('[data-product-card]');
    if (card && !card.contains(event.target)) closeQuickPanel(openPanel, false);
  });

  mqDesktop.addEventListener('change', () => {
    document.querySelectorAll('[data-quick-panel]:not([hidden])').forEach((panel) => closeQuickPanel(panel, false));
    const sheet = quickSheet();
    if (sheet && !sheet.hidden) theme.closeOverlay(sheet, { restoreFocus: false });
  });

  document.addEventListener('click', (event) => {
    const heart = event.target.closest('[data-wishlist-toggle]');
    if (!heart) return;
    event.preventDefault();
    document.dispatchEvent(
      new CustomEvent('theme:wishlist:toggle', {
        detail: {
          button: heart,
          productId: Number(heart.dataset.productId),
          variantId: Number(heart.dataset.variantId),
          handle: heart.dataset.productHandle,
          url: heart.dataset.productUrl,
        },
      })
    );
  });

  document.addEventListener('theme:wishlist:state', (event) => {
    const { productId, saved, count } = event.detail || {};
    if (productId != null) {
      document.querySelectorAll(`[data-wishlist-toggle][data-product-id="${productId}"]`).forEach((heart) => {
        heart.setAttribute('aria-pressed', String(Boolean(saved)));
      });
    }
    if (count != null) {
      document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
        badge.textContent = count > 0 ? String(count) : '';
      });
    }
  });

  const quickBuyTimers = new WeakMap();

  const collapseQuickBuy = (root, focusOpen) => {
    const open = root.querySelector('[data-quick-buy-open]');
    const sizes = root.querySelector('[data-quick-buy-sizes]');
    const status = root.querySelector('[data-quick-buy-status]');
    const hadFocus = root.contains(document.activeElement);
    clearTimeout(quickBuyTimers.get(root));
    sizes.hidden = true;
    status.hidden = true;
    open.hidden = false;
    open.setAttribute('aria-expanded', 'false');
    const drawer = cartDrawer();
    if (drawer?.opener && status.contains(drawer.opener)) drawer.opener = open;
    if (focusOpen || hadFocus) open.focus({ preventScroll: true });
  };

  const showQuickBuyStatus = (root, message) => {
    const status = root.querySelector('[data-quick-buy-status]');
    root.querySelector('[data-quick-buy-message]').textContent = message;
    root.querySelector('[data-quick-buy-sizes]').hidden = true;
    status.hidden = false;
    status.focus({ preventScroll: true });
    clearTimeout(quickBuyTimers.get(root));
    const hide = () => {
      const busy = status.matches(':hover') || document.activeElement === status.querySelector('a');
      if (busy) {
        quickBuyTimers.set(root, setTimeout(hide, 1200));
        return;
      }
      collapseQuickBuy(root, false);
    };
    quickBuyTimers.set(root, setTimeout(hide, 3600));
  };

  document.addEventListener('click', async (event) => {
    const open = event.target.closest('[data-quick-buy-open]');
    if (open) {
      const root = open.closest('[data-quick-buy]');
      const sizes = root.querySelector('[data-quick-buy-sizes]');
      open.setAttribute('aria-expanded', 'true');
      sizes.hidden = false;
      open.hidden = true;
      (sizes.querySelector('[data-quick-buy-variant]:not([disabled])') || sizes.querySelector('button'))?.focus({
        preventScroll: true,
      });
      return;
    }

    const chip = event.target.closest('[data-quick-buy-variant]');
    if (!chip || chip.disabled) return;
    const root = chip.closest('[data-quick-buy]');
    const card = chip.closest('[data-product-card]');
    const title = card?.dataset.productTitle || '';
    const size = chip.dataset.quickBuySize;
    setBusy(chip, true);
    try {
      await theme.cart.add([{ id: Number(chip.dataset.quickBuyVariant), quantity: 1 }]);
      setBusy(chip, false);
      showQuickBuyStatus(root, chip.dataset.quickBuyAdded);
      theme.announce(
        chip.hasAttribute('data-quick-buy-one')
          ? interpolate(strings.added, { title })
          : interpolate(strings.addedWithSize, { title, size })
      );
    } catch (error) {
      setBusy(chip, false);
      theme.announce(error.message || strings.cartError);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented || overlayStack.length) return;
    const sizes = event.target.closest?.('[data-quick-buy-sizes]:not([hidden])');
    if (sizes) collapseQuickBuy(sizes.closest('[data-quick-buy]'), true);
  });

  const hydrateImage = (img) => {
    if (!img || !img.dataset.srcset) return;
    img.sizes = img.dataset.sizes || '100vw';
    img.srcset = img.dataset.srcset;
    img.removeAttribute('data-srcset');
  };

  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  let deferredObserver = null;

  theme.initDeferredImages = (scope = document) => {
    if (!hoverCapable.matches || !('IntersectionObserver' in window)) return;
    deferredObserver =
      deferredObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            deferredObserver.unobserve(entry.target);
            hydrateImage(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      );
    scope.querySelectorAll('img[data-srcset]').forEach((img) => deferredObserver.observe(img));
  };

  const setCardPhoto = (media, index) => {
    if (index > 0) hydrateImage(media.querySelector('.product-card__image--alt'));
    media.dataset.photo = String(index);
    media.querySelectorAll('[data-card-dot]').forEach((dot) => {
      if (Number(dot.dataset.cardDot) === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  document.addEventListener('click', (event) => {
    const dot = event.target.closest('[data-card-dot]');
    if (!dot) return;
    event.preventDefault();
    const media = dot.closest('[data-card-media]');
    media.dataset.photoPinned = dot.dataset.cardDot;
    setCardPhoto(media, Number(dot.dataset.cardDot));
  });

  const hoverMedia = (event) => {
    if (event.pointerType !== 'mouse') return null;
    const media = event.target.closest?.('[data-card-media][data-photos="2"]');
    if (!media || media.contains(event.relatedTarget)) return null;
    return media;
  };

  document.addEventListener('pointerover', (event) => {
    const media = hoverMedia(event);
    if (media) setCardPhoto(media, 1 - Number(media.dataset.photoPinned || 0));
  });

  document.addEventListener('pointerout', (event) => {
    const media = hoverMedia(event);
    if (media) setCardPhoto(media, Number(media.dataset.photoPinned || 0));
  });

  const init = () => {
    theme.initReveal();
    theme.initAmbientVideos();
    syncOverlayTriggers();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  if (document.readyState === 'complete') theme.initDeferredImages();
  else window.addEventListener('load', () => theme.initDeferredImages(), { once: true });

  document.addEventListener('shopify:section:load', (event) => {
    theme.initReveal(event.target);
    theme.initAmbientVideos(event.target);
    theme.initDeferredImages(event.target);
  });
})();
